import { useState, useEffect, useCallback } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface KolPriceData {
  id: string;
  creatorName: string;
  handler: string | null;
  platform: string | null;
  region: string | null;
  currencyType: string;
  isDuplicate: boolean;
  duplicateCount?: number;
  picIds: string[];
  profileImageUrl?: string;
  kolDbId: string;
  contentTypes: Array<{
    type: string;
    price: number;
  }>;
}

interface UsePriceListDataProps {
  searchTerm: string;
  hasFullAccess: boolean;
  currentUserName: string;
  page: number;
  pageSize: number;
}

export function usePriceListData({ 
  searchTerm, 
  hasFullAccess, 
  currentUserName,
  page,
  pageSize
}: UsePriceListDataProps) {
  const [data, setData] = useState<KolPriceData[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const { toast } = useToast();

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      setHasError(false);
      
      console.log("Fetching KOL price data with access control");
      
      // Use RLS to automatically filter based on user access
      let query = supabase
        .from('kol_db')
        .select('*')
        .order('creator_name', { ascending: true });
      
      // Add search filters if search term exists
      if (searchTerm && searchTerm.trim()) {
        query = query.or(`creator_name.ilike.%${searchTerm}%,social_media_handler.ilike.%${searchTerm}%,social_media_type.ilike.%${searchTerm}%,region.ilike.%${searchTerm}%`);
      }
      
      // Get all matching data first to process into individual KOL records
      const { data: kolData, error } = await query;
      
      if (error) {
        console.error("Error fetching KOL data:", error);
        throw error;
      }
      
      console.log(`Fetched ${kolData?.length || 0} KOL records`);
      
      // Transform data - each kol_db record becomes one KolPriceData record
      const transformedData: KolPriceData[] = [];
      
      kolData?.forEach(kol => {
        // Only include KOLs with content types
        if (!Array.isArray(kol.content_type) || kol.content_type.length === 0) {
          return;
        }
        
        // Parse price data - handle both string and object types
        let priceData: Record<string, number> = {};
        if (kol.price_content_type) {
          if (typeof kol.price_content_type === 'string') {
            try {
              priceData = JSON.parse(kol.price_content_type);
            } catch (e) {
              console.error("Failed to parse price data:", e);
              priceData = {};
            }
          } else if (typeof kol.price_content_type === 'object') {
            priceData = kol.price_content_type as Record<string, number>;
          }
        }
        
        // Convert pic_id from Json[] to string[]
        const picIds: string[] = [];
        if (Array.isArray(kol.pic_id)) {
          kol.pic_id.forEach(pic => {
            if (typeof pic === 'string') {
              picIds.push(pic);
            } else if (pic !== null && pic !== undefined) {
              picIds.push(String(pic));
            }
          });
        }
        
        // Create content types array with prices
        const contentTypes = kol.content_type
          .filter((ct: any) => typeof ct === 'string' && ct.trim())
          .map((contentType: string) => ({
            type: contentType,
            price: priceData[contentType] !== undefined ? priceData[contentType] : 0
          }));
        
        // Create individual KOL record - each kol_db record is unique, no duplicates
        const newKolData: KolPriceData = {
          id: kol.id,
          creatorName: kol.creator_name,
          handler: kol.social_media_handler,
          platform: kol.social_media_type,
          region: kol.region,
          currencyType: kol.currency_type || 'USD',
          isDuplicate: false, // Each kol_db record is unique
          duplicateCount: 1, // Each record represents one entry
          picIds: picIds,
          profileImageUrl: kol.profile_image_url,
          kolDbId: kol.id,
          contentTypes: contentTypes
        };
        
        transformedData.push(newKolData);
      });
      
      console.log(`Transformed to ${transformedData.length} individual KOL price records`);
      
      // Set total count based on individual records
      const totalRecords = transformedData.length;
      setTotalCount(totalRecords);
      
      // Apply pagination to the individual records
      const offset = (page - 1) * pageSize;
      const paginatedData = transformedData.slice(offset, offset + pageSize);
      
      setData(paginatedData);
      
    } catch (error: any) {
      console.error("Error in fetchData:", error);
      setHasError(true);
      const errorMessage = error.message || String(error);
      toast({
        title: "Error",
        description: `Failed to load price list data: ${errorMessage}`,
        variant: "destructive"
      });
      setData([]);
    } finally {
      setIsLoading(false);
    }
  }, [searchTerm, hasFullAccess, currentUserName, page, pageSize, toast]);

  const updatePrice = useCallback(async (
    kolDbId: string, 
    contentType: string,
    newPrice: number
  ): Promise<void> => {
    try {
      console.log("usePriceListData: updatePrice called", { kolDbId, contentType, newPrice });
      
      // Optimistically update the local state first for instant feedback
      setData(prevData => {
        const updatedData = prevData.map(kol => {
          if (kol.kolDbId === kolDbId) {
            console.log("usePriceListData: Updating KOL data optimistically", { 
              kolDbId, 
              contentType, 
              newPrice,
              existingContentTypes: kol.contentTypes 
            });
            return {
              ...kol,
              contentTypes: kol.contentTypes.map(ct => 
                ct.type === contentType ? { ...ct, price: newPrice } : ct
              )
            };
          }
          return kol;
        });
        console.log("usePriceListData: Optimistic update complete");
        return updatedData;
      });
      
      // Check for related KOL Tracker records that match this kol_db_id and content type
      console.log("usePriceListData: Searching for kol_tracker records", { kolDbId, contentType });
      const { data: trackerRecords, error: trackerFetchError } = await supabase
        .from('kol_tracker')
        .select('id, name, type, expense')
        .eq('kol_db_id', kolDbId)
        .eq('type', contentType);
      
      if (trackerFetchError) {
        console.error("usePriceListData: Error fetching tracker records:", trackerFetchError);
        throw trackerFetchError;
      }
      
      // Always update the kol_db.price_content_type to persist the price change
      console.log("usePriceListData: Updating kol_db.price_content_type");
      
      // First, get the current price_content_type data
      const { data: currentKolData, error: fetchError } = await supabase
        .from('kol_db')
        .select('price_content_type')
        .eq('id', kolDbId)
        .single();
      
      if (fetchError) {
        console.error("usePriceListData: Error fetching current kol_db data:", fetchError);
        throw fetchError;
      }
      
      // Parse existing price data
      let currentPriceData: Record<string, number> = {};
      if (currentKolData.price_content_type) {
        if (typeof currentKolData.price_content_type === 'string') {
          try {
            currentPriceData = JSON.parse(currentKolData.price_content_type);
          } catch (e) {
            console.error("Failed to parse existing price data:", e);
            currentPriceData = {};
          }
        } else if (typeof currentKolData.price_content_type === 'object') {
          currentPriceData = currentKolData.price_content_type as Record<string, number>;
        }
      }
      
      // Update the price for this content type
      currentPriceData[contentType] = newPrice;
      
      // Update kol_db with the new price data
      const { error: updateKolDbError } = await supabase
        .from('kol_db')
        .update({ price_content_type: currentPriceData })
        .eq('id', kolDbId);
      
      if (updateKolDbError) {
        console.error("usePriceListData: Error updating kol_db:", updateKolDbError);
        throw updateKolDbError;
      }
      
      console.log("usePriceListData: Successfully updated kol_db.price_content_type");
      
      if (!trackerRecords || trackerRecords.length === 0) {
        console.log("usePriceListData: No matching kol_tracker records found for this content type");
        toast({
          title: "Price Updated in Master List",
          description: `Price updated for content type "${contentType}". Note: No posting records found for this content type - add posting records in KOL Tracker to track actual expenses.`,
          variant: "default"
        });
        return;
      }
      
      console.log(`usePriceListData: Found ${trackerRecords.length} kol_tracker records to update:`, trackerRecords);
      
      // Update all matching tracker records
      const { error: trackerUpdateError } = await supabase
        .from('kol_tracker')
        .update({ 
          expense: newPrice,
          updated_date: new Date().toISOString().split('T')[0]
        })
        .eq('kol_db_id', kolDbId)
        .eq('type', contentType);
      
      if (trackerUpdateError) {
        console.error("usePriceListData: Error updating KOL tracker records:", trackerUpdateError);
        throw trackerUpdateError;
      }
      
      console.log(`usePriceListData: Successfully updated ${trackerRecords.length} KOL tracker records`);
      
      toast({
        title: "Price Updated Successfully",
        description: `Updated price for ${trackerRecords.length} posting record${trackerRecords.length > 1 ? 's' : ''} and master price list`,
      });
      
      console.log("usePriceListData: Price update completed successfully");
      
    } catch (error) {
      console.error("usePriceListData: Error updating price:", error);
      // Revert optimistic update on error by refetching data
      await fetchData();
      throw error;
    }
  }, [toast, fetchData]);

  const updateCurrency = useCallback(async (
    kolDbId: string,
    newCurrency: string
  ): Promise<void> => {
    try {
      console.log("usePriceListData: updateCurrency called", { kolDbId, newCurrency });
      
      // Optimistically update the local state first for instant feedback
      setData(prevData => {
        const updatedData = prevData.map(kol => {
          if (kol.kolDbId === kolDbId) {
            console.log("usePriceListData: Updating KOL currency optimistically", { 
              kolDbId, 
              newCurrency,
              previousCurrency: kol.currencyType 
            });
            return {
              ...kol,
              currencyType: newCurrency
            };
          }
          return kol;
        });
        console.log("usePriceListData: Currency optimistic update complete");
        return updatedData;
      });
      
      // Update the kol_db.currency_type field
      console.log("usePriceListData: Updating kol_db.currency_type");
      const { error: updateError } = await supabase
        .from('kol_db')
        .update({ currency_type: newCurrency })
        .eq('id', kolDbId);
      
      if (updateError) {
        console.error("usePriceListData: Error updating currency_type:", updateError);
        throw updateError;
      }
      
      console.log("usePriceListData: Successfully updated kol_db.currency_type");
      
      toast({
        title: "Currency Updated Successfully",
        description: `Currency updated to ${newCurrency}`,
      });
      
    } catch (error) {
      console.error("usePriceListData: Error updating currency:", error);
      // Revert optimistic update on error by refetching data
      await fetchData();
      throw error;
    }
  }, [toast, fetchData]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      fetchData();
    }, 300);
    return () => clearTimeout(debounceTimer);
  }, [fetchData]);

  return {
    data,
    totalCount,
    isLoading,
    hasError,
    refetch: fetchData,
    updatePrice,
    updateCurrency
  };
}
